/**
 * Org-level switches stored on `OrganisationV2.permissions`. Two kinds live here:
 *
 * - Configuration: per-customer policy or contract settings. They stay for as long as the
 *   customer needs them and are never "rolled out".
 * - Feature flags: per-org rollouts of new functionality. Once a feature is generally
 *   available, delete the flag and its gates so the feature is simply on for everyone.
 *
 * Values removed from this enum need no data migration: `getCurrentOrgPermissions` drops
 * unknown values from an org's array on its next toggle.
 */
export enum OrgPermission {
  // Configuration
  RelativityUsage = 'relativityUsage',
  Require2FANonSSO = 'require2faNonSSO',
  ExternalUserTermsOfUse = 'externalUserTermsOfUse',
  UsePropelAdminRoleAsDefault = 'usePropelAdminRoleAsDefault',
  DisableBillingForKimThreads = 'disableBillingForKimThreads',
  /** Read by the ML pipeline (chronos-ml `org_features.py`), not by this backend. */
  AlwaysUseForms = 'alwaysUseForms',
  // Feature flags
  UserBlocking = 'userBlocking',
}

export enum OrgPermissionKind {
  Configuration = 'configuration',
  FeatureFlag = 'featureFlag',
}

export interface OrgPermissionConfig {
  id: string;
  permission: OrgPermission;
  kind: OrgPermissionKind;
  title: string;
  description: string;
}

/** Admin UI sections, in display order. Configuration sits above feature flags. */
export const ORG_PERMISSION_SECTIONS: readonly {
  kind: OrgPermissionKind;
  title: string;
  iconClass: string;
  description: string;
  emptyText: string;
}[] = [
  {
    kind: OrgPermissionKind.Configuration,
    title: 'Organisation configuration',
    iconClass: 'lucide--settings-2',
    description:
      'Per-customer settings. These are not rollouts and stay in place for as long as the contract or policy requires.',
    emptyText: 'No configuration settings.',
  },
  {
    kind: OrgPermissionKind.FeatureFlag,
    title: 'Feature flags',
    iconClass: 'lucide--flag',
    description:
      'Per-organisation rollouts of new functionality. Once a feature is generally available its flag is removed from the code.',
    emptyText: 'No active feature flags. Everything is generally available.',
  },
];

/** Ordered as displayed within each section. */
export const ORG_PERMISSION_CONFIGS: readonly OrgPermissionConfig[] = [
  {
    id: 'relativity-usage-toggle',
    permission: OrgPermission.RelativityUsage,
    kind: OrgPermissionKind.Configuration,
    title: 'Relativity Integration',
    description:
      'Enables the Relativity integration for this organisation. The organisation also needs its Relativity site and client credentials configured in its own settings.',
  },
  {
    id: 'require-2fa-toggle',
    permission: OrgPermission.Require2FANonSSO,
    kind: OrgPermissionKind.Configuration,
    title: 'Require 2FA non-SSO users',
    description:
      'Require non-SSO users to enable two-factor authentication before accessing the platform.',
  },
  {
    id: 'external-user-terms-of-use-toggle',
    permission: OrgPermission.ExternalUserTermsOfUse,
    kind: OrgPermissionKind.Configuration,
    title: 'Require terms of use acceptance for non-SSO users',
    description:
      "Require non-SSO (external) users to accept the organisation's terms of use before using the platform. Terms files (terms.md and terms.pdf) must be uploaded to the org S3 bucket under {orgId}/terms-of-use/.",
  },
  {
    id: 'use-propel-admin-role-as-default-toggle',
    permission: OrgPermission.UsePropelAdminRoleAsDefault,
    kind: OrgPermissionKind.Configuration,
    title: 'Use Propel admin role as default',
    description:
      'When enabled, users added via PropelAuth with the Admin role are assigned the Administrator role in Wexler. When disabled, all new users get the default role.',
  },
  {
    id: 'disable-kim-thread-billing-toggle',
    permission: OrgPermission.DisableBillingForKimThreads,
    kind: OrgPermissionKind.Configuration,
    title: 'Disable billing for Kim threads',
    description:
      'When enabled, Kim threads do not contribute towards billed credits for this organisation — excluded from usage reporting, report downloads, and blocking/limit enforcement. Activity counts remain visible.',
  },
  {
    id: 'always-use-forms-toggle',
    permission: OrgPermission.AlwaysUseForms,
    kind: OrgPermissionKind.Configuration,
    title: 'Always use forms',
    description:
      'Run Textract forms analysis on every document and mine the key-value output for facts alongside regular extraction. Read by the ML pipeline.',
  },
  {
    id: 'user-blocking-toggle',
    permission: OrgPermission.UserBlocking,
    kind: OrgPermissionKind.FeatureFlag,
    title: 'User blocking',
    description:
      'Allow organisation administrators to block and unblock users in their organisation.',
  },
];
